/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/usePickupGroupData.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for loading Pickup Group data and related options (extensions).
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchPickupGroupById } from '../api/pickupGroupsApi';
import { fetchExtensions, Extension } from '../api/extensionsApi';
import { createDefaultFormData, DEFAULT_SETTINGS } from '../utils/formDefaults';
import { generateContextFromName } from '../utils/contextHelpers';
import type { PickupGroupFormState, PickupGroupMember } from '../types';

interface UsePickupGroupDataOptions {
  id?: string;
  duplicateId?: string | null;
  isEditMode: boolean;
}

export function usePickupGroupData({
  id,
  duplicateId,
  isEditMode
}: UsePickupGroupDataOptions) {
  const location = useLocation();
  const [formData, setFormData] = useState<PickupGroupFormState>(createDefaultFormData);
  const [members, setMembers] = useState<PickupGroupMember[]>([]);
  const [availableExtensions, setAvailableExtensions] = useState<Extension[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');

  // Load options (Extensions)
  useEffect(() => {
    const loadOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const extensions = await fetchExtensions();
        setAvailableExtensions(extensions);
      } catch (error) {
        console.error('Error loading extensions:', error);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadOptions();
  }, []);

  // Load pickup group data if editing or duplicating
  const loadPickupGroup = useCallback(async (groupId: string, duplicate: boolean = false) => {
    setIsLoading(true);
    try {
      const group = await fetchPickupGroupById(groupId);

      const settings = (group.settings as Record<string, any>) || {};
      const mergedSettings = {
        pickup_tone: settings.pickup_tone || DEFAULT_SETTINGS.pickup_tone,
        ringback: settings.ringback || DEFAULT_SETTINGS.ringback,
        confirmation: settings.confirmation ?? DEFAULT_SETTINGS.confirmation,
        DND_override: settings.DND_override ?? DEFAULT_SETTINGS.DND_override,
        pickup_timeout: settings.pickup_timeout ?? DEFAULT_SETTINGS.pickup_timeout
      };

      const loadedMembers: PickupGroupMember[] = Array.isArray(group.members)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ? group.members.map((member: any) => ({
            extension: member.extension,
            isMember: member.isMember ?? true,
            allowPickup: member.allowPickup ?? true
          }))
        : [];

      const loadedData: PickupGroupFormState = {
        name: group.name || '',
        context: group.context || generateContextFromName(group.name || ''),
        priority: group.priority ?? 100,
        skipBusy: group.skipBusy ?? false,
        enabled: group.enabled ?? true,
        settings: mergedSettings
      };

      // If duplicating, modify name
      if (duplicate && loadedData.name) {
        loadedData.name = `${loadedData.name} Copy`.trim();
      }

      setFormData(loadedData);
      setMembers(loadedMembers);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load pickup group when id or duplicateId changes
  useEffect(() => {
    const currentId = id || 'new';
    const savedDataKey = 'pickupGroups:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    if (isEditMode && id && id !== 'new') {
      if (lastLoadedIdRef.current !== id) {
        loadPickupGroup(id);
        lastLoadedIdRef.current = id;
        lastLocationKeyRef.current = locationKey;
      }
    } else if (!isEditMode && duplicateId) {
      if (lastLoadedIdRef.current !== duplicateId) {
        loadPickupGroup(duplicateId, true);
        lastLoadedIdRef.current = duplicateId;
        lastLocationKeyRef.current = locationKey;
      }
    } else if (!isEditMode && !duplicateId) {
      // Check if we need to reset:
      // 1. ID changed
      // 2. Location key changed (React Router navigation)
      // 3. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ? 
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;
      
      const shouldReset = lastLoadedIdRef.current !== 'new' || 
                          lastLocationKeyRef.current !== locationKey ||
                          (lastLoadedIdRef.current === 'new' && currentId === 'new' && 
                           resetTimestamp && resetTimestamp !== lastResetTimestamp);
      
      if (shouldReset) {
        // Clear any saved data to ensure fresh form
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }
        
        // Reset form to defaults
        setFormData(createDefaultFormData());
        setMembers([]);
        lastLoadedIdRef.current = 'new';
        lastLocationKeyRef.current = locationKey;
      }
    }
  }, [id, isEditMode, duplicateId, loadPickupGroup, location.key, location.pathname, location.search]);

  return {
    formData,
    setFormData,
    members,
    setMembers,
    availableExtensions,
    isLoading,
    isLoadingOptions,
    loadPickupGroup
  };
}

